import type { FieldDefinition } from '@/shared/components/query-builder'
import type { FieldConfigType } from '@/shared/components/query-builder/types'
import type { UseControllerProps } from 'react-hook-form'
import type { DataChoicesRegistry } from '../filter/types'

import React from 'react'
import { merge } from 'lodash/fp'
import { HelpCircleIcon, PlusIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { formatFilterRule } from '@/entities/column'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	getFiledConfig,
	QueryBuilder,
	QueryBuilderAddButton,
	QueryBuilderEmpty,
	QueryBuilderRulesList,
} from '@/shared/components/query-builder'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import {
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { useAppStore } from '@/shared/contexts/app'

import { getLocalization } from '../filter/localization'

export default function RHFQueryBuilder({
	name,
	label,
	shouldUnregister,
	defaultValue,
	fields,
	dataChoices,
	extendedFieldConfig,
	irremovableCount,
	isLoading,
	hint,
	description,
	enableReferencing,
}: UseControllerProps & {
	isLoading?: boolean
	label?: string
	fields: FieldDefinition[]
	hint?: string
	description?: string
	dataChoices?: DataChoicesRegistry
	extendedFieldConfig?: FieldConfigType
	irremovableCount?: number
	enableReferencing?: boolean
}) {
	const { t } = useTranslation()

	const dateFormat = useAppStore((state) => state.dateFormat)
	const { control } = useFormContext()

	return (
		<FormField
			name={name}
			defaultValue={defaultValue}
			shouldUnregister={shouldUnregister}
			control={control}
			render={({ field, fieldState: { error } }) => (
				<FormItem>
					<FormLabel>
						{label}
						{hint && (
							<Tooltip>
								<TooltipTrigger asChild>
									<HelpCircleIcon className='size-3 text-muted-foreground' />
								</TooltipTrigger>
								<TooltipContent side='top'>{hint}</TooltipContent>
							</Tooltip>
						)}
					</FormLabel>
					{isLoading ? (
						<div className='flex flex-col space-y-2'>
							<Skeleton className='h-10 w-full rounded-lg' />
							<Skeleton className='h-10 w-full rounded-lg' />
						</div>
					) : (
						<ErrorBoundary
							fallback={() => (
								<Alert variant='destructive'>
									<AlertDescription>
										🐞{' '}
										{t('ui_fragment_error', {
											defaultValue:
												'Oops ... it looks like an unexpected error has occurred. Our team`s been notified.',
										})}
									</AlertDescription>
								</Alert>
							)}
						>
							<QueryBuilder
								dateFormat={dateFormat}
								rules={field.value || []}
								errorSchema={error as unknown as any[]}
								irremovableCount={irremovableCount}
								onRulesChange={(rules) =>
									field.onChange(rules.map(formatFilterRule))
								}
								fieldConfig={merge(
									getFiledConfig(fields, dataChoices || {}),
									extendedFieldConfig
								)}
								enableReferencing={enableReferencing}
								locale={getLocalization(t)}
							>
								<QueryBuilderEmpty className='border-b' />
								<QueryBuilderRulesList className='max-h-[400px] overflow-y-auto border-y py-2' />
								<div className='flex items-center gap-2'>
									<QueryBuilderAddButton data-testid='filters-popover-add'>
										<PlusIcon />
										{t('filters_add_filter')}
									</QueryBuilderAddButton>
								</div>
							</QueryBuilder>
						</ErrorBoundary>
					)}

					{!!description && <FormDescription>{description}</FormDescription>}
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}
